// ChatModule.jsx - Refactored Chat Module for OkBot
import React, { useState, useEffect, useRef } from 'react';
import { isPlaceholderMedia } from '../utils/responseMedia';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  MessageSquare, Mic, MicOff, ArrowUp, Clock, RefreshCw, Share2, 
  Globe, Sliders, Database, Volume2, VolumeX, History, X,
  Calendar, Bell, Image as ImageIcon, Video, MapPin, ExternalLink, HelpCircle, MessageCircle, Search
} from 'lucide-react';
import ShareModal from './ShareModal';
import ChatHistory from './ChatHistory';
import Lightbox from './Lightbox';
import { UserSupportChat, SupportChatButton } from './SupportChatModal';
/* WhatsAppLinkModal removed — WA modal is now inline in ChatModule */

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ==================== SHARE PANEL ====================
const SharePanel = ({ content, waPhone }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  // Cerrar al click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const plainText = (content || '').replace(/!\[([^\]]*)\]\([^)]+\)/g, '[$1]').replace(/\*\*([^*]+)\*\*/g, '$1').replace(/#+\s/g, '').trim();
  const shortText = plainText.slice(0, 280);
  const encoded  = encodeURIComponent(shortText);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(plainText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
    setOpen(false);
  };

  const shareOptions = [
    {
      label: copied ? '✓ Copiado' : 'Copiar texto',
      icon: '📋',
      action: copyToClipboard,
      color: 'text-gray-300',
    },
    {
      label: 'WhatsApp',
      icon: '💬',
      action: () => { window.open(`https://wa.me/?text=${encoded}`, '_blank'); setOpen(false); },
      color: 'text-green-400',
    },
    // Si tiene WhatsApp vinculado: enviar directo a su propio número
    ...(waPhone ? [{
      label: `Mi WA (${waPhone.slice(-4)})`,
      icon: '📲',
      action: () => { window.open(`https://wa.me/${waPhone.replace(/\D/g,'')}?text=${encoded}`, '_blank'); setOpen(false); },
      color: 'text-green-300',
    }] : []),
    {
      label: 'Twitter / X',
      icon: '✕',
      action: () => { window.open(`https://twitter.com/intent/tweet?text=${encoded}`, '_blank'); setOpen(false); },
      color: 'text-sky-400',
    },
    {
      label: 'Telegram',
      icon: '✈️',
      action: () => { window.open(`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encoded}`, '_blank'); setOpen(false); },
      color: 'text-blue-400',
    },
    {
      label: 'Facebook',
      icon: '👍',
      action: () => { window.open(`https://www.facebook.com/sharer/sharer.php?quote=${encoded}&u=${encodeURIComponent(window.location.href)}`, '_blank'); setOpen(false); },
      color: 'text-blue-500',
    },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-400 transition-colors"
        title="Compartir"
      >
        <Share2 size={13} />
        <span>Compartir</span>
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1.5 z-50 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[170px]">
          {shareOptions.map((opt, i) => (
            <button
              key={i}
              onClick={opt.action}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-white/8 transition-colors text-left ${opt.color}`}
            >
              <span className="text-sm">{opt.icon}</span>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== PERSON CARD (Google Knowledge Panel style) ====================
const PersonCard = ({ data, onMediaClick }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [failedUrls, setFailedUrls] = useState(new Set());
  const fetchingPhotos = false; // Missing entity photos are not replaced by stock portraits.

  const tabs = data.categories || ['Información general'];
  const facts = data.facts || [];
  const works = data.works || [];
  const basePhotos = [data.photo, ...(data.photos || [])].filter(
    url => typeof url === 'string' && !isPlaceholderMedia(url)
  );
  const allPhotos = basePhotos.slice(0, 8);
  // Solo mostrar fotos que no fallaron
  const photos = allPhotos.filter(url => !failedUrls.has(url));

  const handleImgError = (e, url) => {
    const el = e.target;
    const PROXY = `${API}/proxy-image?url=${encodeURIComponent(url)}`;
    if (!el.src.includes('/proxy-image?')) {
      // Primera falla: reintentar vía proxy del backend
      el.src = PROXY;
      el.onerror = () => { setFailedUrls(prev => new Set([...prev, url])); };
    } else {
      // Proxy también falló: ocultar
      setFailedUrls(prev => new Set([...prev, url]));
    }
  };

  return (
    <div className="mt-2 mb-2 rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04] backdrop-blur-sm shadow-xl max-w-[420px]">
      {/* Header: Name + title */}
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-xl font-bold text-white leading-tight">{data.name}</h2>
        {data.title && <p className="text-sm text-gray-400 mt-0.5">{data.title}</p>}
      </div>

      {/* Category chips */}
      {tabs.length > 1 && (
        <div className="px-4 pb-2 flex gap-2 flex-wrap">
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`text-xs px-3 py-1 rounded-full border transition-all ${
                activeTab === i
                  ? 'bg-white/15 border-white/30 text-white font-medium'
                  : 'bg-transparent border-white/15 text-gray-400 hover:border-white/25 hover:text-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Photo section — loading skeleton */}
      {fetchingPhotos && photos.length === 0 && (
        <div className="flex gap-1 px-4 pb-3">
          <div className="flex-1 h-48 rounded-xl bg-white/5 animate-pulse flex items-center justify-center">
            <span className="text-gray-600 text-xs">Buscando fotos…</span>
          </div>
        </div>
      )}

      {/* Photo section — no photo fallback */}
      {!fetchingPhotos && photos.length === 0 && (
        <div className="flex gap-1 px-4 pb-3">
          <div className="flex-1 h-44 rounded-xl bg-white/5 border border-white/8 flex flex-col items-center justify-center gap-2">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center text-2xl font-bold text-gray-500">
              {data.name ? data.name.charAt(0).toUpperCase() : '?'}
            </div>
            <span className="text-gray-600 text-xs">Sin foto disponible</span>
          </div>
        </div>
      )}

      {/* Photo section — gallery with lightbox */}
      {photos.length > 0 && (
        <div className="flex gap-1 px-4 pb-3">
          {/* Main photo */}
          <div className="flex-1 relative group cursor-pointer" onClick={() => onMediaClick && onMediaClick(photos[0], 'image', photos)}>
            <img
              src={photos[0]}
              alt={data.name}
              className="w-full h-48 object-cover rounded-xl transition-all shadow-lg group-hover:brightness-75"
              onError={(e) => handleImgError(e, photos[0])}
            />
            <div className="absolute inset-0 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-black/60 rounded-full p-2.5">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </div>
            </div>
            {photos.length > 1 && (
              <div className="absolute bottom-1.5 right-1.5 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
                1 / {photos.length}
              </div>
            )}
            {data.photo_credit && (
              <span className="absolute bottom-1.5 left-1.5 text-[10px] text-white/60 bg-black/40 px-1.5 py-0.5 rounded">
                {data.photo_credit}
              </span>
            )}
          </div>

          {/* Thumbnail strip — clicking opens gallery at that index */}
          {photos.length > 1 && (
            <div className="flex flex-col gap-1 w-[68px]">
              {photos.slice(1, photos.length >= 4 ? 4 : photos.length).map((url, i) => (
                <div key={i} className="relative group cursor-pointer"
                     onClick={() => onMediaClick && onMediaClick(url, 'image', photos)}>
                  <img
                    src={url}
                    alt={`${data.name} ${i + 2}`}
                    className="w-full h-[58px] object-cover rounded-lg transition-all group-hover:brightness-75"
                    onError={(e) => handleImgError(e, url)}
                  />
                  <div className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-black/60 rounded-full p-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    </div>
                  </div>
                  {/* Show count on last thumb if more photos exist */}
                  {i === 2 && photos.length > 4 && (
                    <div className="absolute inset-0 rounded-lg bg-black/60 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">+{photos.length - 4}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Facts grid */}
      {facts.length > 0 && (
        <div className="px-4 pb-3 grid grid-cols-2 gap-2">
          {facts.map((f, i) => (
            <div key={i} className="bg-white/5 rounded-xl px-3 py-2.5 border border-white/8">
              <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide mb-0.5">{f.label}</p>
              <p className="text-sm text-white font-semibold leading-tight">{f.value}</p>
              {f.sub && <p className="text-[11px] text-gray-500 mt-0.5">{f.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Works/thumbnails (movies, albums, etc.) */}
      {works.length > 0 && (
        <div className="px-4 pb-3">
          <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide mb-2">
            {data.works_label || 'Obras destacadas'}
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
            {works.map((w, i) => {
              const workThumbs = works.filter(x => x.thumb).map(x => x.thumb);
              return (
                <div key={i} className="flex-shrink-0 w-14">
                  {w.thumb ? (
                    <div className="relative group cursor-pointer"
                         onClick={() => onMediaClick && onMediaClick(w.thumb, 'image', workThumbs)}>
                      <img
                        src={w.thumb}
                        alt={w.title}
                        className="w-14 h-14 rounded-lg object-cover transition-all group-hover:brightness-75"
                        title={w.title}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-14 h-14 rounded-lg bg-white/5 flex items-center justify-center text-gray-600 text-[10px] text-center px-1 cursor-pointer hover:bg-white/10 transition-colors"
                      onClick={() => w.url && window.open(w.url, '_blank')}
                    >
                      {w.title?.slice(0, 20)}
                    </div>
                  )}
                  {w.year && <p className="text-[10px] text-gray-500 mt-0.5 text-center">{w.year}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bio */}
      {data.bio && (
        <div className="px-4 pb-3">
          <div className="bg-white rounded-xl px-3 py-2.5 border border-gray-200 shadow-sm">
            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide mb-1.5">Descripción</p>
            <p className="text-sm text-gray-800 leading-relaxed">{data.bio}</p>
            {data.source_url && (
              <a
                href={data.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-1.5 text-[11px] text-blue-600 hover:text-blue-500 font-medium transition-colors"
              >
                {data.source || 'Wikipedia'} →
              </a>
            )}
          </div>
        </div>
      )}

      {/* Social / links footer */}
      {(data.social || data.search_url) && (
        <div className="px-4 pb-4 flex gap-2 flex-wrap">
          {data.social?.instagram && (
            <a href={data.social.instagram} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-gradient-to-r from-pink-500/20 to-purple-500/20 hover:from-pink-500/30 hover:to-purple-500/30 border border-pink-500/20 px-2.5 py-1.5 rounded-full text-pink-300 hover:text-pink-200 transition-all">
              📸 Instagram
            </a>
          )}
          {data.social?.twitter && (
            <a href={data.social.twitter} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1.5 rounded-full text-gray-400 hover:text-white transition-all">
              𝕏 Twitter/X
            </a>
          )}
          {data.social?.linkedin && (
            <a href={data.social.linkedin} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-blue-600/15 hover:bg-blue-600/25 border border-blue-500/20 px-2.5 py-1.5 rounded-full text-blue-400 hover:text-blue-300 transition-all">
              💼 LinkedIn
            </a>
          )}
          {data.social?.youtube && (
            <a href={data.social.youtube} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-red-600/15 hover:bg-red-600/25 border border-red-500/20 px-2.5 py-1.5 rounded-full text-red-400 hover:text-red-300 transition-all">
              ▶ YouTube
            </a>
          )}
          {data.social?.tiktok && (
            <a href={data.social.tiktok} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1.5 rounded-full text-gray-400 hover:text-white transition-all">
              🎵 TikTok
            </a>
          )}
          {data.social?.facebook && (
            <a href={data.social.facebook} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-blue-700/15 hover:bg-blue-700/25 border border-blue-600/20 px-2.5 py-1.5 rounded-full text-blue-400 hover:text-blue-300 transition-all">
              👤 Facebook
            </a>
          )}
          {data.social?.website && (
            <a href={data.social.website} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1.5 rounded-full text-gray-400 hover:text-white transition-all">
              🌐 Web oficial
            </a>
          )}
          {data.search_url && (
            <a href={data.search_url} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1.5 text-[11px] bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1.5 rounded-full text-gray-400 hover:text-white transition-all">
              🔍 Buscar más
            </a>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== CINEMA CARD — Google-style template ====================
const CinemaCard = ({ data, onMediaClick }) => {
  const [activeSection, setActiveSection] = useState('info');
  const [posterErr, setPosterErr] = useState(false);
  const [trailerOpen, setTrailerOpen] = useState(false);
  const allPosters = [data.poster, ...(data.posters || [])].filter(Boolean);
  const cast = data.cast || [];
  const streaming = data.streaming || [];
  const facts = data.facts || [];
  const genres = data.genres || [];
  const clips = data.clips || [];

  const extractYtId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    return m ? m[1] : null;
  };
  const ytId = extractYtId(data.trailer_url);
  const extraClips = clips.map(c => ({ ...c, ytId: extractYtId(c.url) })).filter(c => c.ytId);

  // Streaming logos by name
  const streamingColors = {
    netflix: '#E50914', 'amazon prime': '#00A8E0', 'disney+': '#1130B4', 'hbo max': '#8100FF',
    hbo: '#8100FF', 'apple tv+': '#000000', 'peacock': '#E4A500', 'paramount+': '#0064FF',
    hulu: '#3DBB3D', 'star+': '#0070F3', 'crunchyroll': '#FF6600', 'mubi': '#161616',
  };

  return (
    <div className="mt-2 mb-2 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1117] to-[#06080d] shadow-2xl max-w-[460px]">

      {/* ── HERO: Poster grande + info lateral ── */}
      <div className="flex gap-0">
        {/* Poster */}
        <div className="flex-shrink-0 w-[140px]">
          {!posterErr && allPosters[0] ? (
            <img
              src={allPosters[0]}
              alt={data.title}
              className="w-full h-[210px] object-cover cursor-pointer"
              onClick={() => onMediaClick && onMediaClick(allPosters[0], 'image')}
              onError={() => setPosterErr(true)}
            />
          ) : (
            <div className="w-full h-[210px] bg-white/5 flex items-center justify-center text-5xl">🎬</div>
          )}
        </div>

        {/* Info lateral */}
        <div className="flex-1 px-3 pt-3 pb-2 min-w-0 flex flex-col gap-1.5">
          <h2 className="text-lg font-bold text-white leading-tight">{data.title}</h2>

          {/* Meta row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {data.year && <span className="text-gray-400 text-xs font-medium">{data.year}</span>}
            {data.runtime && <><span className="text-gray-600 text-xs">·</span><span className="text-gray-400 text-xs">{data.runtime}</span></>}
            {data.media_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 border border-blue-500/25 font-medium">{data.media_type}</span>
            )}
            {data.age_rating && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-gray-400">{data.age_rating}</span>
            )}
          </div>

          {/* Ratings */}
          <div className="flex items-center gap-2 flex-wrap">
            {data.rating?.imdb && (
              <div className="flex items-center gap-1 bg-yellow-500/15 border border-yellow-500/20 rounded-lg px-2 py-1">
                <span className="text-yellow-400 text-xs font-bold">IMDb</span>
                <span className="text-yellow-300 text-xs font-semibold">{data.rating.imdb}</span>
              </div>
            )}
            {data.rating?.rt && (
              <div className="flex items-center gap-1 bg-red-500/15 border border-red-500/20 rounded-lg px-2 py-1">
                <span className="text-xs">🍅</span>
                <span className="text-red-300 text-xs font-semibold">{data.rating.rt}</span>
              </div>
            )}
            {data.rating?.mc && (
              <div className="flex items-center gap-1 bg-green-500/15 border border-green-500/20 rounded-lg px-2 py-1">
                <span className="text-green-400 text-xs font-bold">MC</span>
                <span className="text-green-300 text-xs font-semibold">{data.rating.mc}</span>
              </div>
            )}
          </div>

          {/* Genres */}
          {genres.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {genres.slice(0, 4).map((g, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-gray-400">{g}</span>
              ))}
            </div>
          )}

          {/* Director */}
          {data.director && (
            <p className="text-xs text-gray-500"><span className="text-gray-400 font-medium">Dir: </span>{data.director}</p>
          )}

          {/* Starring */}
          {data.starring && (
            <p className="text-xs text-gray-500 leading-tight" style={{display: '-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>
              <span className="text-gray-400 font-medium">Con: </span>{data.starring}
            </p>
          )}

          {/* Trailer button */}
          {ytId && !trailerOpen && (
            <button
              onClick={() => setTrailerOpen(true)}
              className="mt-auto flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-semibold transition-all self-start"
            >
              ▶ Ver tráiler
            </button>
          )}
        </div>
      </div>

      {/* ── Thumbnail strip (extra posters/stills) — gallery with navigation ── */}
      {allPosters.length > 1 && (
        <div className="flex gap-1 px-3 pt-2 overflow-x-auto scrollbar-thin pb-1">
          {allPosters.slice(1, 7).map((url, i) => (
            <div key={i} className="relative group flex-shrink-0 cursor-pointer"
                 onClick={() => onMediaClick && onMediaClick(url, 'image', allPosters)}>
              <img src={url} alt={`${data.title} ${i + 2}`}
                className="w-[70px] h-[45px] object-cover rounded-lg transition-all border border-white/5 group-hover:brightness-75"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
              <div className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Trailer embed ── */}
      {ytId && trailerOpen && (
        <div className="px-3 pt-2 pb-1">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">Tráiler oficial</p>
            <button onClick={() => setTrailerOpen(false)} className="text-gray-600 hover:text-gray-400 text-xs">✕ Cerrar</button>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} className="w-full aspect-video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen title="Tráiler" />
          </div>
        </div>
      )}

      {/* ── Section tabs ── */}
      <div className="px-3 pt-2 flex gap-1 border-b border-white/5">
        {[
          { id: 'info', label: 'Sinopsis' },
          { id: 'cast', label: `Reparto${cast.length ? ` (${cast.length})` : ''}` },
          { id: 'watch', label: 'Dónde ver' },
          ...(extraClips.length ? [{ id: 'clips', label: `Clips` }] : []),
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveSection(tab.id)}
            className={`text-xs px-3 py-1.5 border-b-2 transition-all font-medium ${activeSection === tab.id ? 'border-red-500 text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Info section ── */}
      {activeSection === 'info' && (
        <div className="px-3 py-3 space-y-3">
          {/* Synopsis */}
          {data.bio && (
            <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm">
              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-1.5">Sinopsis</p>
              <p className="text-sm text-gray-800 leading-relaxed">{data.bio}</p>
              {data.source_url && (
                <a href={data.source_url} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-2 text-[11px] text-blue-600 font-medium hover:underline">
                  {data.source || 'Ver más'} →
                </a>
              )}
            </div>
          )}

          {/* Facts grid */}
          {facts.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {facts.map((f, i) => (
                <div key={i} className="bg-white/[0.04] rounded-xl px-3 py-2 border border-white/[0.06]">
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide mb-0.5">{f.label}</p>
                  <p className="text-xs text-white font-semibold leading-tight">{f.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Cast section ── */}
      {activeSection === 'cast' && (
        <div className="px-3 py-3">
          {cast.length > 0 ? (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {cast.map((actor, i) => (
                <div key={i} className="flex-shrink-0 text-center w-[72px]">
                  {actor.photo ? (
                    <img src={actor.photo} alt={actor.name}
                      className="w-[68px] h-[68px] rounded-xl object-cover shadow-md border border-white/10 hover:opacity-80 transition-all cursor-pointer"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-[68px] h-[68px] rounded-xl bg-white/5 flex items-center justify-center text-2xl border border-white/10">👤</div>
                  )}
                  <p className="text-[10px] text-gray-300 mt-1 leading-tight font-medium" style={{display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{actor.name}</p>
                  {actor.role && <p className="text-[9px] text-gray-600 leading-tight">{actor.role}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-sm text-center py-4">Sin información de reparto</p>
          )}
        </div>
      )}

      {/* ── Streaming section ── */}
      {activeSection === 'watch' && (
        <div className="px-3 py-3">
          {streaming.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {streaming.map((s, i) => {
                const bgColor = Object.entries(streamingColors).find(([k]) => s.name?.toLowerCase().includes(k))?.[1];
                return (
                  <a key={i} href={s.url || '#'} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 transition-all hover:border-white/20 text-sm text-white min-w-[120px]"
                    style={{ backgroundColor: bgColor ? `${bgColor}20` : 'rgba(255,255,255,0.04)' }}>
                    {s.logo && <img src={s.logo} alt={s.name} className="w-6 h-6 object-contain rounded-md" onError={(e) => { e.target.style.display = 'none'; }} />}
                    <div>
                      <p className="text-xs font-semibold leading-tight">{s.name}</p>
                      {s.type && <p className="text-[10px] text-gray-500 leading-tight capitalize">{s.type}</p>}
                    </div>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-2xl mb-2">📺</p>
              <p className="text-gray-500 text-sm">Sin información de streaming disponible</p>
            </div>
          )}
        </div>
      )}

      {/* ── Clips section ── */}
      {activeSection === 'clips' && extraClips.length > 0 && (
        <div className="px-3 py-3 space-y-2">
          {extraClips.slice(0, 3).map((clip, i) => (
            <div key={i} className="rounded-xl overflow-hidden shadow-lg">
              {clip.title && <p className="text-xs text-gray-500 mb-1 px-0.5">{clip.title}</p>}
              <iframe src={`https://www.youtube.com/embed/${clip.ytId}`} className="w-full aspect-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title={clip.title || 'Clip'} />
            </div>
          ))}
        </div>
      )}

      {/* ── Footer links ── */}
      <div className="px-3 pb-3 pt-1 flex gap-2 flex-wrap border-t border-white/5 mt-1">
        {data.imdb_url && (
          <a href={data.imdb_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[11px] bg-yellow-500/10 border border-yellow-500/20 px-2.5 py-1 rounded-full text-yellow-400 hover:text-yellow-300 transition-all">
            ⭐ IMDb
          </a>
        )}
        {data.rt_url && (
          <a href={data.rt_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-[11px] bg-red-500/10 border border-red-500/20 px-2.5 py-1 rounded-full text-red-400 hover:text-red-300 transition-all">
            🍅 Rotten Tomatoes
          </a>
        )}
        {data.search_url && (
          <a href={data.search_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[11px] bg-white/5 border border-white/10 px-2.5 py-1 rounded-full text-gray-400 hover:text-white transition-all">
            🔍 Más info
          </a>
        )}
      </div>
    </div>
  );
};

// ==================== RICH MESSAGE COMPONENT ====================
const RichMessage = ({ content, onMediaClick }) => {
  // Parse markdown-style formatting
  
  // Detect and parse code blocks
  const parseCodeBlocks = (text) => {
    const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = codeBlockRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'codeblock', language: match[1], code: match[2].trim() });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Parse images: ![alt](url)
  const parseImages = (text) => {
    const imgRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = imgRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'image', alt: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };
  
  // Parse markdown links: [text](url)
  const parseLinks = (text) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'link', text: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Parse bullet lists
  const parseLists = (text) => {
    const lines = text.split('\n');
    const parts = [];
    let currentList = [];
    let listType = null;
    
    lines.forEach((line, idx) => {
      const bulletMatch = line.match(/^[\s]*[-*•]\s+(.+)/);
      const numberMatch = line.match(/^[\s]*(\d+)[.)]\s+(.+)/);
      
      if (bulletMatch) {
        if (listType !== 'bullet' && currentList.length > 0) {
          parts.push({ type: listType === 'number' ? 'numbered-list' : 'bullet-list', items: currentList });
          currentList = [];
        }
        listType = 'bullet';
        currentList.push(bulletMatch[1]);
      } else if (numberMatch) {
        if (listType !== 'number' && currentList.length > 0) {
          parts.push({ type: 'bullet-list', items: currentList });
          currentList = [];
        }
        listType = 'number';
        currentList.push(numberMatch[2]);
      } else {
        if (currentList.length > 0) {
          parts.push({ type: listType === 'number' ? 'numbered-list' : 'bullet-list', items: currentList });
          currentList = [];
          listType = null;
        }
        if (line.trim()) {
          parts.push({ type: 'text', content: line });
        }
      }
    });
    
    if (currentList.length > 0) {
      parts.push({ type: listType === 'number' ? 'numbered-list' : 'bullet-list', items: currentList });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const isImageUrl = (url) => {
    if (/\.(jpg|jpeg|png|gif|webp|svg|bmp|avif|tiff)(\?.*)?$/i.test(url)) return true;
    if (url.includes('picsum.photos')) return true;
    if (url.includes('imgur.com') && !url.includes('/a/')) return true;
    if (url.includes('cloudinary.com') && url.includes('/image/')) return true;
    if (url.includes('images.unsplash.com')) return true;
    if (url.includes('source.unsplash.com')) return true;
    if (url.includes('images.pexels.com')) return true;
    if (url.includes('i.redd.it')) return true;
    if (url.includes('media.giphy.com')) return true;
    if (url.includes('placeholder.com')) return true;
    if (url.includes('loremflickr.com')) return true;
    if (url.includes('/api/generated-images/')) return true;
    if (url.includes('googleusercontent.com')) return true;
    if (url.includes('gstatic.com') && (url.includes('image') || url.includes('photo'))) return true;
    if (url.includes('wikimedia.org') && !url.includes('/wiki/')) return true;
    if (url.includes('wikipedia.org/wiki/Special:FilePath')) return true;
    if (url.includes('upload.wikimedia.org')) return true;
    if (url.includes('ggpht.com')) return true;
    if (url.includes('tmdb.org/t/p/')) return true;
    if (url.includes('image.tmdb.org')) return true;
    if (url.includes('media-amazon.com/images/')) return true;
    if (url.includes('fanart.tv')) return true;
    if (url.includes('thetvdb.com/banners/')) return true;
    if (url.includes('imgflip.com')) return true;
    if (url.includes('flickr.com/photos/') && url.includes('_')) return true;
    if (url.includes('staticflickr.com')) return true;
    if (url.includes('pinimg.com/originals/')) return true;
    if (url.includes('cdn.pixabay.com')) return true;
    if (url.includes('images.nasa.gov')) return true;
    if (url.includes('apod.nasa.gov/apod/image')) return true;
    return false;
  };
  
  const isVideoUrl = (url) => {
    if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url)) return true;
    if (url.includes('youtube.com') || url.includes('youtu.be')) return true;
    if (url.includes('vimeo.com')) return true;
    return false;
  };
  
  const isMapUrl = (url) => url.includes('maps.google') || url.includes('goo.gl/maps') || url.includes('google.com/maps');

  const extractBareUrls = (text) => {
    const urlRegex = /(https?:\/\/[^\s<>\[\]()]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      const url = match[1].replace(/[.,;:!?)]+$/, '');
      parts.push({ type: 'url', url: url });
      lastIndex = match.index + url.length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  // Parse bold and italic text
  const parseFormatting = (text) => {
    // Bold: **text** or __text__
    let formatted = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-white">$1</strong>');
    formatted = formatted.replace(/__([^_]+)__/g, '<strong class="font-semibold text-white">$1</strong>');
    // Italic: *text* or _text_
    formatted = formatted.replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
    formatted = formatted.replace(/_([^_]+)_/g, '<em class="italic">$1</em>');
    // Inline code: `code`
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="bg-white/10 px-1.5 py-0.5 rounded text-red-400 text-sm font-mono">$1</code>');
    return formatted;
  };

  // ── Generic bracket-balanced extractor for [TAG:{...}] cards ─────────────────
  const extractTaggedCards = (text, startTag, cardType) => {
    const segments = [];
    let pos = 0;
    while (pos < text.length) {
      const tagIdx = text.indexOf(startTag, pos);
      if (tagIdx === -1) break;
      if (tagIdx > pos) {
        const pre = text.slice(pos, tagIdx).trim();
        if (pre) segments.push({ type: 'text', content: pre });
      }
      const jsonStart = tagIdx + startTag.length;
      let depth = 0, jsonEnd = -1, inStr = false, esc = false;
      for (let i = jsonStart; i < text.length; i++) {
        const ch = text[i];
        if (esc) { esc = false; continue; }
        if (ch === '\\' && inStr) { esc = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) {
            if (text[i + 1] === ']') { jsonEnd = i; pos = i + 2; }
            else { pos = tagIdx + startTag.length; }
            break;
          }
        }
      }
      if (jsonEnd === -1) { pos = tagIdx + startTag.length; continue; }
      const jsonStr = text.slice(jsonStart, jsonEnd + 1);
      try {
        const data = JSON.parse(jsonStr);
        segments.push({ type: cardType, data });
      } catch {
        try {
          const fixed = jsonStr.replace(/,\s*([}\]])/g, '$1');
          const data = JSON.parse(fixed);
          segments.push({ type: cardType, data });
        } catch {
          segments.push({ type: 'text', content: text.slice(tagIdx, pos) });
        }
      }
    }
    if (pos < text.length) {
      const tail = text.slice(pos).trim();
      if (tail) segments.push({ type: 'text', content: tail });
    }
    return segments.length > 0 ? segments : [{ type: 'text', content: text }];
  };

  // ── PERSON CARD: detect [PERSON_CARD:{...}] with bracket-balanced extraction ──
  const extractPersonCards = (text) => {
    const segments = [];
    let pos = 0;
    while (pos < text.length) {
      const startTag = '[PERSON_CARD:';
      const tagIdx = text.indexOf(startTag, pos);
      if (tagIdx === -1) break;

      // Push text before the tag
      if (tagIdx > pos) {
        const pre = text.slice(pos, tagIdx).trim();
        if (pre) segments.push({ type: 'text', content: pre });
      }

      // Find the balanced closing } ] using depth counting
      const jsonStart = tagIdx + startTag.length;
      let depth = 0;
      let jsonEnd = -1;
      let inStr = false;
      let esc = false;
      for (let i = jsonStart; i < text.length; i++) {
        const ch = text[i];
        if (esc) { esc = false; continue; }
        if (ch === '\\' && inStr) { esc = true; continue; }
        if (ch === '"') { inStr = !inStr; continue; }
        if (inStr) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) {
            // check next char is ]
            if (text[i + 1] === ']') {
              jsonEnd = i;
              pos = i + 2; // skip }]
            } else {
              // malformed — skip this tag
              pos = tagIdx + startTag.length;
            }
            break;
          }
        }
      }
      if (jsonEnd === -1) {
        // Could not find balanced end
        pos = tagIdx + startTag.length;
        continue;
      }
      const jsonStr = text.slice(jsonStart, jsonEnd + 1);
      try {
        const data = JSON.parse(jsonStr);
        segments.push({ type: 'person_card', data });
      } catch {
        // Try to repair common model mistakes: trailing comma before }
        try {
          const fixed = jsonStr
            .replace(/,\s*([}\]])/g, '$1')  // trailing commas
            .replace(/'/g, "\u2019");         // smart quotes in bio won't break JSON
          const data = JSON.parse(fixed);
          segments.push({ type: 'person_card', data });
        } catch {
          segments.push({ type: 'text', content: text.slice(tagIdx, pos) });
        }
      }
    }
    if (pos < text.length) {
      const tail = text.slice(pos).trim();
      if (tail) segments.push({ type: 'text', content: tail });
    }
    return segments.length > 0 ? segments : [{ type: 'text', content: text }];
  };

  // Process content step by step
  const processContent = () => {
    // Zero: extract PERSON_CARD and CINEMA_CARD blocks first
    let pcSegments = extractPersonCards(content);
    // Also extract CINEMA_CARD from any text segments
    const mixedSegments = [];
    pcSegments.forEach(seg => {
      if (seg.type !== 'text') { mixedSegments.push(seg); return; }
      const cinemaSegs = extractTaggedCards(seg.content, '[CINEMA_CARD:', 'cinema_card');
      mixedSegments.push(...cinemaSegs);
    });
    pcSegments = mixedSegments;

    // If there are person/cinema cards, process each text segment through normal pipeline
    if (pcSegments.some(s => s.type === 'person_card' || s.type === 'cinema_card')) {
      const result = [];
      pcSegments.forEach(seg => {
        if (seg.type === 'person_card' || seg.type === 'cinema_card') {
          result.push(seg);
        } else if (seg.content) {
          // run normal pipeline on remaining text
          const codeBlockParsed = parseCodeBlocks(seg.content);
          codeBlockParsed.forEach(part => {
            if (part.type === 'codeblock') { result.push(part); }
            else if (part.type === 'text') {
              const imgParsed = parseImages(part.content);
              imgParsed.forEach(ip => {
                if (ip.type === 'image') { result.push(ip); }
                else {
                  const listParsed = parseLists(ip.content);
                  listParsed.forEach(lp => {
                    if (lp.type === 'bullet-list' || lp.type === 'numbered-list') { result.push(lp); }
                    else {
                      const linkParsed = parseLinks(lp.content);
                      linkParsed.forEach(pp => {
                        if (pp.type === 'text') {
                          const urlParts = extractBareUrls(pp.content);
                          urlParts.forEach(up => {
                            if (up.type === 'url') result.push({ type: 'link', text: up.url, url: up.url, isBareUrl: true });
                            else result.push(up);
                          });
                        } else { result.push(pp); }
                      });
                    }
                  });
                }
              });
            }
          });
        }
      });
      return result;
    }

    // First: parse code blocks
    const codeBlockParsed = parseCodeBlocks(content);
    const allParts = [];
    
    codeBlockParsed.forEach(part => {
      if (part.type === 'codeblock') {
        allParts.push(part);
      } else if (part.type === 'text') {
        // Parse images
        const imageParsed = parseImages(part.content);
        
        imageParsed.forEach(imgPart => {
          if (imgPart.type === 'image') {
            allParts.push(imgPart);
          } else if (imgPart.type === 'text') {
            // Parse lists within text
            const listParsed = parseLists(imgPart.content);
            
            listParsed.forEach(listPart => {
              if (listPart.type === 'bullet-list' || listPart.type === 'numbered-list') {
                allParts.push(listPart);
              } else if (listPart.type === 'text') {
                // Parse links
                const linkParsed = parseLinks(listPart.content);
                
                linkParsed.forEach(linkPart => {
                  if (linkPart.type === 'text') {
                    // Extract bare URLs
                    const urlParts = extractBareUrls(linkPart.content);
                    urlParts.forEach(up => {
                      if (up.type === 'url') {
                        allParts.push({ type: 'link', text: up.url, url: up.url, isBareUrl: true });
                      } else {
                        allParts.push(up);
                      }
                    });
                  } else {
                    allParts.push(linkPart);
                  }
                });
              }
            });
          }
        });
      }
    });
    
    return allParts;
  };

  const finalParts = processContent();
  
  return (
    <div className="space-y-2 leading-relaxed">
      {finalParts.map((part, idx) => {
        // Person Knowledge Card
        if (part.type === 'person_card') {
          return <PersonCard key={idx} data={part.data} onMediaClick={onMediaClick} />;
        }

        // Cinema / Series / Anime Knowledge Card
        if (part.type === 'cinema_card') {
          return <CinemaCard key={idx} data={part.data} onMediaClick={onMediaClick} />;
        }

        // Code block
        if (part.type === 'codeblock') {
          return (
            <div key={idx} className="mt-3 mb-3">
              <div className="bg-black/40 rounded-lg overflow-hidden">
                {part.language && (
                  <div className="bg-white/5 px-3 py-1 text-xs text-gray-400 border-b border-white/5">
                    {part.language}
                  </div>
                )}
                <pre className="p-3 text-sm text-gray-300 overflow-x-auto font-mono">
                  <code>{part.code}</code>
                </pre>
              </div>
            </div>
          );
        }
        
        // Bullet list
        if (part.type === 'bullet-list') {
          return (
            <ul key={idx} className="mt-2 mb-2 space-y-1.5 ml-1">
              {part.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">•</span>
                  <span dangerouslySetInnerHTML={{ __html: parseFormatting(item) }} />
                </li>
              ))}
            </ul>
          );
        }
        
        // Numbered list
        if (part.type === 'numbered-list') {
          return (
            <ol key={idx} className="mt-2 mb-2 space-y-1.5 ml-1">
              {part.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-red-400 font-semibold min-w-[20px]">{i + 1}.</span>
                  <span dangerouslySetInnerHTML={{ __html: parseFormatting(item) }} />
                </li>
              ))}
            </ol>
          );
        }
        
        // Image from markdown — with lightbox zoom overlay
        if (part.type === 'image') {
          if (isPlaceholderMedia(part.url)) return null;
          return (
            <div key={idx} className="mt-3 mb-3 inline-block">
              <div className="relative group rounded-xl overflow-hidden cursor-pointer shadow-lg max-w-[320px]"
                   onClick={() => onMediaClick(part.url, 'image')}>
                <img 
                  src={part.url} 
                  alt={part.alt || 'Imagen'} 
                  className="max-w-[320px] max-h-[240px] object-cover transition-transform group-hover:scale-105 block"
                  onError={(e) => {
                    const el = e.target;
                    const proxy = `${API}/proxy-image?url=${encodeURIComponent(part.url)}`;
                    if (!el.src.includes('/proxy-image?')) { el.src = proxy; el.onerror = () => { el.parentElement.parentElement.style.display = 'none'; }; }
                    else { el.parentElement.parentElement.style.display = 'none'; }
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </div>
                </div>
              </div>
              {part.alt && <p className="text-xs text-gray-500 mt-1.5 max-w-[320px]">{part.alt}</p>}
            </div>
          );
        }
        
        // Links
        if (part.type === 'link') {
          // Check if it's an image URL — with lightbox zoom overlay
          if (isImageUrl(part.url)) {
            return (
              <div key={idx} className="mt-3 mb-3 inline-block">
                <div className="relative group rounded-xl overflow-hidden cursor-pointer shadow-lg max-w-[300px]"
                     onClick={() => onMediaClick(part.url, 'image')}>
                  <img 
                    src={part.url} 
                    alt={part.text || 'Imagen'} 
                    className="max-w-[300px] max-h-[220px] object-cover transition-transform group-hover:scale-105 block"
                    onError={(e) => {
                      const el = e.target;
                      const proxy = `${API}/proxy-image?url=${encodeURIComponent(part.url)}`;
                      if (!el.src.includes('/proxy-image?')) { el.src = proxy; el.onerror = () => { el.parentElement.parentElement.style.display = 'none'; }; }
                      else { el.parentElement.parentElement.style.display = 'none'; }
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    </div>
                  </div>
                </div>
                {!part.isBareUrl && <p className="text-xs text-gray-500 mt-1.5">{part.text}</p>}
              </div>
            );
          }
          
          // YouTube video — embedded player with fullscreen support
          if (isVideoUrl(part.url)) {
            const youtubeMatch = part.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
            if (youtubeMatch) {
              return (
                <div key={idx} className="mt-3 mb-3">
                  <div className="relative rounded-xl overflow-hidden max-w-[400px] shadow-lg bg-black border border-white/10">
                    <iframe 
                      src={`https://www.youtube.com/embed/${youtubeMatch[1]}`} 
                      className="w-full aspect-video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      allowFullScreen 
                      title="YouTube video"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 font-medium">▶ YouTube</span>
                    {!part.isBareUrl && part.text !== part.url && (
                      <p className="text-xs text-gray-500">{part.text}</p>
                    )}
                    <a href={part.url} target="_blank" rel="noopener noreferrer"
                       className="ml-auto text-[10px] text-gray-600 hover:text-gray-400 flex items-center gap-0.5">
                      Abrir <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                  </div>
                </div>
              );
            }
            
            // Vimeo video
            const vimeoMatch = part.url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
            if (vimeoMatch) {
              return (
                <div key={idx} className="mt-3 mb-3">
                  <div className="relative rounded-xl overflow-hidden max-w-[400px] shadow-lg bg-black border border-white/10">
                    <iframe
                      src={`https://player.vimeo.com/video/${vimeoMatch[1]}?badge=0&autopause=0&player_id=0`}
                      className="w-full aspect-video"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                      allowFullScreen
                      title="Vimeo video"
                    />
                  </div>
                  {!part.isBareUrl && part.text !== part.url && (
                    <p className="text-xs text-gray-500 mt-1.5">{part.text}</p>
                  )}
                </div>
              );
            }

            // Regular video file — native player
            if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(part.url)) {
              return (
                <div key={idx} className="mt-3 mb-3">
                  <video src={part.url} controls className="rounded-xl max-w-[400px] shadow-lg border border-white/10" preload="metadata">
                    Tu navegador no soporta video
                  </video>
                </div>
              );
            }
          }
          
          // Google Maps link
          if (isMapUrl(part.url)) {
            return (
              <a key={idx} href={part.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2.5 rounded-xl hover:bg-blue-500/30 transition-colors my-1 border border-blue-500/30">
                <MapPin size={16} />{part.isBareUrl ? 'Ver en Google Maps' : part.text}
                <ExternalLink size={12} />
              </a>
            );
          }
          
          // Regular link with visual card style for bare URLs
          if (part.isBareUrl) {
            return (
              <a key={idx} href={part.url} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg transition-colors my-1 text-sm border border-white/10 max-w-fit">
                <ExternalLink size={14} className="text-red-400 flex-shrink-0" />
                <span className="text-gray-300 truncate max-w-[280px]">
                  {part.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 50)}{part.url.length > 60 ? '...' : ''}
                </span>
              </a>
            );
          }
          
          // Named link
          return (
            <a key={idx} href={part.url} target="_blank" rel="noopener noreferrer"
               className="text-red-400 hover:text-red-300 underline underline-offset-2 decoration-red-400/50 inline-flex items-center gap-1 transition-colors">
              {part.text}
              <ExternalLink size={12} />
            </a>
          );
        }
        
        // Regular text with formatting
        return (
          <span 
            key={idx} 
            dangerouslySetInnerHTML={{ __html: parseFormatting(part.content) }} 
          />
        );
      })}
    </div>
  );
};

// ==================== MAIN CHAT MODULE ====================
const ChatModule = ({ user, navigate, theme = 'dark', onModuleChange }) => {
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('okbot_last_conv_msgs');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(() => {
    try { return localStorage.getItem('okbot_last_conv_id') || null; } catch { return null; }
  });
  const [listening, setListening] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [modelType, setModelType] = useState("combo-free");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(() => {
    try { return localStorage.getItem('okbot_autospeak') === 'true'; } catch { return false; }
  });
  const [fishAudioReady, setFishAudioReady] = useState(null); // null=unknown, true=ok, false=no key
  const fishAudioRef = useRef(null);
  // ttsLanguage is synced with global UI language (okbot_ui_lang), falls back to tts-specific pref
  const [ttsLanguage, setTtsLanguage] = useState(() => {
    try { return localStorage.getItem('okbot_ui_lang') || localStorage.getItem('okbot_tts_lang') || 'es'; } catch { return 'es'; }
  });
  const [showLangPicker, setShowLangPicker] = useState(false);
  const langPickerRef = useRef(null);
  const [dynamicModels, setDynamicModels] = useState([]);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [userPhoto, setUserPhoto] = useState(null);
  // ── Google CSE Search modal ──────────────────────────────────────────────────────
  const gcseDialogRef = useRef(null);
  const openGCSEModal = () => {
    if (gcseDialogRef.current) {
      gcseDialogRef.current.showModal();
      // Inject Google CSE script only once
      if (!document.getElementById('gcse-script')) {
        const s = document.createElement('script');
        s.id = 'gcse-script';
        s.async = true;
        s.src = 'https://cse.google.com/cse.js?cx=c7be21d2f0c584ba8';
        document.head.appendChild(s);
      }
    }
  };
  const closeGCSEModal = () => gcseDialogRef.current?.close();

  // ── WhatsApp modal — uses native <dialog> showModal() for guaranteed visibility ──
  const [waStep, setWaStep]           = useState(1);   // 1=phone | 2=verify | 'linked'
  const [waCountry, setWaCountry]     = useState('+52');
  const [waPhone, setWaPhone]         = useState('');
  const [waCode, setWaCode]           = useState('');       // input del usuario (manual)
  const [waCode6, setWaCode6]         = useState('');       // código generado (siempre visible)
  const [waWaLink, setWaWaLink]       = useState('');       // wa.me pre-llenado
  const [waOkbotPhone, setWaOkbotPhone] = useState('');
  const [waLinkedPhone, setWaLinkedPhone] = useState('');
  const [waSending, setWaSending]     = useState(false);
  const [waVerifying, setWaVerifying] = useState(false);
  const [waDots, setWaDots]           = useState('');
  const [waWaSent, setWaWaSent]       = useState(false);  // usuario ya tocó el botón WA
  const waDialogRef = useRef(null);
  const waCodeInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const speechSynthRef = useRef(null);

  // Build AI_MODELS from dynamic + static
  const AI_MODELS = [
    { id: "combo-free", name: "⚡ Combo Esencial", badge: "" },
    { id: "super-combo", name: "🚀 Super Combo", badge: "Pro" },
    { id: "combo-paid", name: "💎 Combo Premium", badge: "Pro" },
    { id: "nvidia", name: "🟢 NVIDIA NIM Ultra", badge: "NVIDIA" },
    ...dynamicModels.map(m => ({ id: m.id, name: m.name, badge: m.provider || "" })),
    { id: "gpt", name: "GPT-5.2", badge: "Universal" },
    { id: "gemini", name: "Gemini 3", badge: "Universal" },
  ];

  // Load user profile photo
  useEffect(() => {
    const loadUserPhoto = async () => {
      if (user?.id) {
        try {
          const res = await axios.get(`${API}/users/${user.id}/profile-photo`);
          if (res.data.has_photo) {
            setUserPhoto(`data:${res.data.photo.mime_type};base64,${res.data.photo.data}`);
          }
        } catch (e) {
          console.log('No profile photo');
        }
      }
    };
    loadUserPhoto();
  }, [user]);

  // Load dynamic models from backend
  useEffect(() => {
    const loadModels = async () => {
      try {
        const res = await axios.get(`${API}/admin/ai-agents/openrouter/models`);
        const free = res.data.models || [];
        const paid = res.data.paid_models || [];
        setDynamicModels([...free, ...paid]);
      } catch (e) {
        // Silent fail - static combos still available
      }
    };
    loadModels();
  }, []);

  // Check Fish Audio availability on mount
  useEffect(() => {
    axios.get(`${API}/voice/fish-tts/status`)
      .then(r => setFishAudioReady(r.data?.configured === true))
      .catch(() => setFishAudioReady(false));
  }, []);

  // Sync ttsLanguage when global UI language changes
  useEffect(() => {
    setTtsLanguage(language);
  }, [language]);

  // Persist autoSpeak preference
  useEffect(() => {
    try { localStorage.setItem('okbot_autospeak', autoSpeak ? 'true' : 'false'); } catch {}
  }, [autoSpeak]);

  // Persist TTS language preference
  useEffect(() => {
    try { localStorage.setItem('okbot_tts_lang', ttsLanguage); } catch {}
  }, [ttsLanguage]);

  // Close language picker on outside click
  useEffect(() => {
    if (!showLangPicker) return;
    const handler = (e) => {
      if (langPickerRef.current && !langPickerRef.current.contains(e.target)) setShowLangPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLangPicker]);

  // Quick actions
  const quickActions = [
    { id: "create_reminder", label: "Crea un recordatorio", icon: Bell, action: "Quiero crear un recordatorio para mí. Pregúntame qué recordatorio necesito y cuándo.", moduleId: "reminders", color: "orange" },
    { id: "create_event", label: "Crea un evento", icon: Calendar, action: "Quiero añadir un evento a mi calendario. Pregúntame los detalles del evento.", moduleId: "calendar", color: "blue" },
    { id: "whatsapp_link", label: "Envíame un WhatsApp", icon: MessageCircle, action: null, special: "whatsapp", color: "green" },
    { id: "google_search_btn", label: "Busca en Google", icon: Search, action: null, special: "google_search", color: "red" },
  ];

  useEffect(() => { 
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); 
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  // ── Persist last conversation to localStorage ──────────────────────────────
  useEffect(() => {
    try {
      if (conversationId) localStorage.setItem('okbot_last_conv_id', conversationId);
      else localStorage.removeItem('okbot_last_conv_id');
    } catch {}
  }, [conversationId]);

  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem('okbot_last_conv_msgs', JSON.stringify(messages.slice(-60)));
      }
    } catch {}
  }, [messages]);

  // ── Fish Audio / ElevenLabs / browser TTS cascade ──────────────────────────
  const cleanForTTS = (text) =>
    text
      .replace(/!\[.*?\]\(.*?\)/g, '')
      .replace(/\[([^\]]+)\]\(.*?\)/g, '$1')
      .replace(/https?:\/\/[^\s]+/g, '')
      .replace(/[#*_~`]/g, '')
      .trim();

  // ── Robust audio player: fetch → Blob URL → <audio> ──────────────────────
  const _playBlobAudio = (blobUrl, onDone) => {
    const audio = new Audio(blobUrl);
    fishAudioRef.current = audio;
    const cleanup = () => {
      URL.revokeObjectURL(blobUrl);
      fishAudioRef.current = null;
      onDone();
    };
    audio.onended = cleanup;
    audio.onerror = cleanup;
    // Return play promise — callers must await + catch
    return audio.play();
  };

  const speakResponse = async (text) => {
    const cleanText = cleanForTTS(text);
    if (!cleanText) { toast.info("No hay texto para leer"); return; }

    // Stop anything already playing
    if (fishAudioRef.current) {
      fishAudioRef.current.pause();
      try { fishAudioRef.current.src = ''; } catch {}
      fishAudioRef.current = null;
    }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setIsSpeaking(true);

    // ── 1️⃣  Fish Audio via streaming endpoint (Blob URL — no data-URI limits) ──
    if (fishAudioReady !== false) {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const params = new URLSearchParams({
          text: cleanText.slice(0, 4000),
          language: ttsLanguage || 'es',
        });
        const resp = await fetch(
          `${backendUrl}/api/voice/fish-tts/stream?${params}`,
          { method: 'GET' }
        );
        if (resp.ok) {
          const blob = await resp.blob();
          if (blob.size > 100) {
            const blobUrl = URL.createObjectURL(blob);
            await _playBlobAudio(blobUrl, () => setIsSpeaking(false));
            return; // ✅ success
          }
        }
        if (resp.status === 503) {
          // Key not configured
          setFishAudioReady(false);
        } else if (resp.status === 402) {
          // No credits — mark unavailable this session
          setFishAudioReady(false);
          console.warn('Fish Audio: no credits (402) — using browser TTS');
        }
      } catch (e) {
        console.warn('Fish Audio stream failed:', e);
      }
    }

    // ── 2️⃣  ElevenLabs (secondary) ───────────────────────────────────────────
    try {
      const res = await axios.post(`${API}/voice/tts`, { text: cleanText });
      if (res.data?.success && res.data?.audio_url) {
        // Convert base64 data-URI → Blob URL for reliable mobile/desktop playback
        const dataUrl = res.data.audio_url;
        const b64 = dataUrl.split(',')[1];
        const mime = dataUrl.split(';')[0].split(':')[1] || 'audio/mpeg';
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: mime });
        const blobUrl = URL.createObjectURL(blob);
        await _playBlobAudio(blobUrl, () => setIsSpeaking(false));
        return;
      }
    } catch (e) {
      console.warn('ElevenLabs TTS failed, using browser TTS:', e);
    }

    // ── 3️⃣  Browser speechSynthesis (guaranteed final fallback) ───────────────
    fallbackSpeech(cleanText);
  };
  
  const fallbackSpeech = (text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      // Map ttsLanguage code to BCP-47 locale for browser speechSynthesis
      const langMap = {
        auto: 'es-MX', es: 'es-MX', en: 'en-US', pt: 'pt-BR',
        fr: 'fr-FR', de: 'de-DE', it: 'it-IT', ja: 'ja-JP',
        zh: 'zh-CN', ko: 'ko-KR', ar: 'ar-SA', ru: 'ru-RU',
        nl: 'nl-NL', tr: 'tr-TR', pl: 'pl-PL', id: 'id-ID',
      };
      utterance.lang = langMap[ttsLanguage] || 'es-MX';
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      speechSynthRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    } else {
      setIsSpeaking(false);
      toast.error("Tu navegador no soporta síntesis de voz");
    }
  };

  const stopSpeaking = () => {
    if (fishAudioRef.current) {
      fishAudioRef.current.pause();
      fishAudioRef.current = null;
    }
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const sendMessage = async (text = input, detail = false) => {
    if (!text.trim() || loading) return;

    if (!detail) {
      setMessages(prev => [...prev, { role: "user", content: text }]);
    }
    setInput("");
    setLoading(true);

    try {
      // All model selectors use the same server-side history, tools and safeguards.
      const endpoint = `${API}/chat/send`;
      
      const response = await axios.post(endpoint, {
        user_id: user.id, 
        message: text, 
        conversation_id: conversationId, 
        agent_type: "general",
        model: modelType,
        detail: detail,
        language: language,
      });
      setConversationId(response.data.conversation_id);
      
      const rawResponse = response.data.response || "";
      
      // Parse and execute any tool calls embedded in the response
      const { cleanedText, executed } = await executeToolCalls(rawResponse);
      
      const assistantMessage = { 
        role: "assistant", 
        content: cleanedText || rawResponse,
        richContent: response.data.rich_content,
        suggestedActions: response.data.suggested_actions,
        model: response.data.model_used || modelType,
        superFast: response.data.super_fast || false,
        originalQuery: text,
        executedActions: executed,
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // Auto-speak with Fish Audio if enabled
      if (autoSpeak) {
        const ttsText = cleanForTTS(cleanedText || rawResponse);
        if (ttsText) setTimeout(() => speakResponse(ttsText), 200);
      }
    } catch (e) {
      const status = e.response?.status;
      const errorDetail = e.response?.data?.detail;
      let message = t('sendError');
      if (status === 401) {
        message = "Tu sesión venció o no es válida. Vuelve a iniciar sesión para continuar.";
      } else if (status === 403) {
        message = "Tu sesión no tiene permiso para enviar este mensaje.";
      } else if (status === 503 && errorDetail?.code === "AI_RESPONSE_UNAVAILABLE") {
        message = errorDetail.message || "Los proveedores de IA no están disponibles temporalmente.";
      } else if (status === 429) {
        message = "Se alcanzó el límite de solicitudes. Espera antes de intentarlo de nuevo.";
      } else if (!e.response) {
        message = "No se pudo confirmar la respuesta por un problema de conexión. Revisa las acciones antes de repetirlas.";
      }
      toast.error(message, { duration: 7000 });
      // Keep the request available to edit. Never automatically replay an
      // action after an HTTP error or an uncertain network result.
      if (!detail) setInput(current => current || text);
      console.error("Chat request failed", { status: status || "network", code: errorDetail?.code });
    } finally {
      setLoading(false);
    }
  };

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const handleVoice = async () => {
    // If currently recording, stop and let onstop handler upload
    if (listening && mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch(e) {}
      return;
    }
    // Try high-accuracy path: MediaRecorder → backend Gemini transcription
    if (navigator.mediaDevices && window.MediaRecorder) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
        audioChunksRef.current = [];
        mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        mr.onstop = async () => {
          setListening(false);
          stream.getTracks().forEach(t => t.stop());
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          if (blob.size < 1000) { toast.error('Audio muy corto'); return; }
          const reader = new FileReader();
          reader.onloadend = async () => {
            const b64 = (reader.result || '').toString().split(',')[1] || '';
            try {
              toast.loading('Transcribiendo audio…', { id: 'tx' });
              const r = await axios.post(`${API}/voice/transcribe`, {
                user_id: user?.id || 'anon',
                audio_base64: b64,
              });
              toast.dismiss('tx');
              const text = (r.data?.transcribed_text || '').replace(/oye okbot|hey okbot/gi, '').trim();
              if (text) { sendMessage(text); } else { toast.error(t('noAudio')); }
            } catch (e) {
              toast.dismiss('tx');
              toast.error(t('transcribeError'));
            }
          };
          reader.readAsDataURL(blob);
        };
        mediaRecorderRef.current = mr;
        mr.start();
        setListening(true);
        toast.success(t('recording'), { duration: 2000 });
        return;
      } catch (e) {
        // Fallback to webkit STT
      }
    }
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Navegador no soportado para audio');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-MX';
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript.replace(/oye okbot|hey okbot/gi, '').trim();
      if (transcript) sendMessage(transcript);
    };
    recognition.start();
  };

  const openWAModal = () => {
    setWaStep(1); setWaPhone(''); setWaCode(''); setWaCode6('');
    setWaWaLink(''); setWaOkbotPhone(''); setWaLinkedPhone('');
    setWaSending(false); setWaVerifying(false); setWaDots('');
    if (waDialogRef.current) {
      try { waDialogRef.current.showModal(); } catch(e) {}
    }
    if (user?.id) {
      axios.get(`${API}/whatsapp/user/status/${user.id}`)
        .then(r => { if (r.data?.linked) { setWaLinkedPhone(r.data.phone || ''); setWaStep('linked'); } })
        .catch(() => {});
    }
  };

  const closeWAModal = () => {
    try { waDialogRef.current?.close(); } catch(e) {}
  };

  // Polling dots animation while waiting
  useEffect(() => {
    if (waStep !== 2) return;
    const id = setInterval(() => setWaDots(d => d.length >= 3 ? '' : d + '.'), 600);
    return () => clearInterval(id);
  }, [waStep]);

  // Poll every 3s for auto-verification via Brain webhook (user sent WA message)
  useEffect(() => {
    if (waStep !== 2 || !user?.id) return;
    const id = setInterval(() => {
      axios.get(`${API}/whatsapp/user/status/${user.id}`)
        .then(r => {
          if (r.data?.linked) {
            setWaLinkedPhone(r.data.phone || '');
            setWaStep('linked');
            toast.success('¡WhatsApp vinculado con OkBot! 🎉');
          }
        }).catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [waStep, user?.id]);

  // PASO 1 → 2: llama al API, obtiene código y wa_link, avanza
  const handleWARequestLink = async () => {
    const digits = waPhone.replace(/\D/g, '');
    if (digits.length < 7) { toast.error('Ingresa un número de teléfono válido'); return; }
    setWaSending(true);
    try {
      const r = await axios.post(`${API}/whatsapp/user/request-link`, {
        user_id:   user.id,
        phone:     `${waCountry}${digits}`,
        user_name: user.name || user.username || 'Usuario',
      });
      setWaCode6(r.data.code || '');          // código SIEMPRE visible en pantalla
      setWaWaLink(r.data.wa_link || '');      // wa.me pre-llenado con "OkBot verificar XXXXXX"
      setWaOkbotPhone(r.data.okbot_phone || '');
      setWaStep(2);
      if (r.data.outbound_sent) {
        toast.success('¡Código enviado a tu WhatsApp!');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al generar el código. Intenta de nuevo.');
    } finally { setWaSending(false); }
  };

  // PASO 2: verificación manual (el usuario escribe el código)
  const handleWAVerify = async () => {
    if (waCode.length !== 6) { toast.error('Ingresa los 6 dígitos del código'); return; }
    setWaVerifying(true);
    try {
      const r = await axios.post(`${API}/whatsapp/user/verify-code`, {
        user_id: user.id,
        code:    waCode,
      });
      if (r.data?.verified || r.data?.success) {
        setWaLinkedPhone(r.data.phone || `${waCountry}${waPhone.replace(/\D/g,'')}`);
        setWaOkbotPhone(r.data.okbot_phone || waOkbotPhone);
        setWaStep('linked');
        toast.success('¡WhatsApp vinculado exitosamente!');
      } else { toast.error('Código incorrecto. Intenta de nuevo.'); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Código incorrecto o expirado');
    } finally { setWaVerifying(false); }
  };

  const handleWAUnlink = async () => {
    if (!window.confirm('¿Desvincular tu WhatsApp de OkBot?')) return;
    try {
      await axios.delete(`${API}/whatsapp/user/unlink/${user.id}`);
      setWaStep(1); setWaLinkedPhone(''); setWaCode6('');
      toast.success('WhatsApp desvinculado');
    } catch { toast.error('Error al desvincular'); }
  };

  // ── Auto-focus input al entrar al paso 2 ──
  useEffect(() => {
    if (waStep === 2) {
      // Pequeño delay para que el DOM se monte
      const t = setTimeout(() => waCodeInputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [waStep]);

  // ── Polling: auto-verifica cuando Jelou Brain webhook confirma el link ──
  useEffect(() => {
    if (waStep !== 2 || !user?.id) return;
    let cancelled = false;
    let dotsIdx = 0;
    const DOTS = ['', '.', '..', '...'];

    const dotsTimer = setInterval(() => {
      if (!cancelled) { dotsIdx = (dotsIdx + 1) % DOTS.length; setWaDots(DOTS[dotsIdx]); }
    }, 600);

    const pollTimer = setInterval(async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/whatsapp/user/verify-status/${user.id}`);
        if (r.data?.linked) {
          setWaLinkedPhone(r.data.phone || '');
          setWaOkbotPhone(r.data.okbot_phone || waOkbotPhone);
          setWaStep('linked');
          toast.success('¡WhatsApp vinculado automáticamente! 🎉');
        }
      } catch { /* silencioso */ }
    }, 3000);

    return () => { cancelled = true; clearInterval(dotsTimer); clearInterval(pollTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waStep, user?.id]);

  const handleQuickAction = (action) => {
    if (action.special === 'whatsapp') {
      openWAModal();
    } else if (action.special === 'google_search') {
      openGCSEModal();
    } else if (action.moduleId && onModuleChange) {
      onModuleChange(action.moduleId);
    } else if (action.action) {
      setInput(action.action);
      textareaRef.current?.focus();
    }
  };

  const executeToolCalls = async (responseText) => {
    const toolCallRegex = /\[TOOL_CALL:(\{[^[\]]*?\})\]/g;
    let match;
    const executed = [];
    const cleanedText = responseText.replace(/\[TOOL_CALL:\{[^[\]]*?\}\]/g, '').trim();
    while ((match = toolCallRegex.exec(responseText)) !== null) {
      try {
        const toolCall = JSON.parse(match[1]);
        const result = await axios.post(`${API}/agent/execute-action`, {
          action_type: toolCall.type,
          action_data: toolCall.data || {},
          user_id: user.id,
        });
        executed.push({ type: toolCall.type, success: true, message: result.data.message });
        toast.success(result.data.message || `✅ Acción ejecutada`);
      } catch (e) {
        console.error('Tool call error:', e);
      }
    }
    return { cleanedText, executed };
  };

  const loadConversation = (conv) => {
    setConversationId(conv.conversation_id);
    setMessages(conv.messages || []);
    setShowHistory(false);
  };

  return (
    <div className="chat-container-centered flex flex-col h-full relative" data-testid="chat-module">
      {/* Lightbox — gallery-aware with navigation */}
      {lightboxMedia && (
        <Lightbox
          isOpen={true}
          onClose={() => setLightboxMedia(null)}
          content={lightboxMedia.items ? lightboxMedia.items[lightboxMedia.idx] : lightboxMedia.content}
          type={lightboxMedia.type}
          items={lightboxMedia.items || null}
          currentIndex={lightboxMedia.idx || 0}
          onNavigate={(idx) => setLightboxMedia(prev => ({ ...prev, idx }))}
        />
      )}

      {/* History Popup */}
      {showHistory && (
        <div className="absolute inset-0 z-30 bg-black/80 backdrop-blur-sm flex items-center justify-center animate-fade-in">
          <div className="glass-panel w-[95%] max-w-lg overflow-hidden rounded-2xl" style={{ height: 'min(85vh, 680px)' }}>
            <div className="h-full overflow-y-auto">
              <ChatHistory user={user} onSelectConversation={loadConversation} onClose={() => setShowHistory(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        itemType="conversation"
        itemId={conversationId}
        itemTitle="Conversación con OkBot"
        itemData={{ userId: user.id }}
      />

      {/* ══ WhatsApp Native Dialog — browser top-layer, bypasses z-index ══ */}
      <dialog
        ref={waDialogRef}
        onClick={(e) => { if (e.target === waDialogRef.current) closeWAModal(); }}
        style={{ border:'none', borderRadius:22, padding:0, width:'calc(100% - 32px)',
          maxWidth:430, overflow:'hidden', boxShadow:'0 28px 70px rgba(0,0,0,0.55)',
          position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          margin:0, maxHeight:'92vh', overflowY:'auto' }}>
          <div style={{ background:'#ffffff' }}>

            {/* ── HEADER ── */}
            <div style={{ background:'linear-gradient(135deg,#16a34a,#15803d)',
              padding:'20px 22px 16px', position:'relative' }}>
              <button onClick={closeWAModal} style={{
                position:'absolute', top:12, right:12,
                background:'rgba(255,255,255,.2)', border:'none', borderRadius:9,
                width:30, height:30, cursor:'pointer', color:'#fff', fontSize:18,
                display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:44, height:44, borderRadius:14,
                  background:'rgba(255,255,255,.2)',
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:22 }}>
                  💬
                </div>
                <div>
                  <div style={{ color:'#fff', fontWeight:800, fontSize:17 }}>OkBot en WhatsApp</div>
                  <div style={{ color:'rgba(255,255,255,.8)', fontSize:12 }}>
                    {waStep === 1 && 'Vincula tu número personal con OkBot'}
                    {waStep === 2 && 'Confirma tu número con el código'}
                    {waStep === 'linked' && '¡Número conectado! ✓'}
                  </div>
                </div>
              </div>
              {waStep !== 'linked' && (
                <div style={{ display:'flex', gap:5, marginTop:14, justifyContent:'center' }}>
                  {[1,2].map(s => (
                    <div key={s} style={{
                      width: s === waStep ? 22 : 7, height:7, borderRadius:4,
                      background: s <= waStep ? '#fff' : 'rgba(255,255,255,.35)',
                      transition:'all .25s',
                    }}/>
                  ))}
                </div>
              )}
            </div>

            {/* ── BODY ── */}
            <div style={{ padding:'22px 22px 20px' }}>

              {/* ════ PASO 1: Teléfono ════ */}
              {waStep === 1 && (
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  <div style={{ background:'#f0fdf4', border:'1.5px solid #bbf7d0',
                    borderRadius:12, padding:'12px 14px' }}>
                    <p style={{ color:'#166534', fontWeight:700, fontSize:13, margin:'0 0 4px' }}>
                      ¿Cómo funciona?
                    </p>
                    <p style={{ color:'#15803d', fontSize:12.5, margin:0, lineHeight:1.6 }}>
                      Ingresa tu número → OkBot genera un código → lo envías por WhatsApp → ¡listo! Tarda menos de 1 minuto.
                    </p>
                  </div>

                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#374151', marginBottom:6 }}>
                      Tu número de WhatsApp
                    </div>
                    <div style={{ display:'flex', gap:0, height:48 }}>
                      <select value={waCountry} onChange={e => setWaCountry(e.target.value)}
                        style={{ flexShrink:0, width:90, border:'1.5px solid #d1d5db',
                          borderRight:'none', borderRadius:'11px 0 0 11px',
                          padding:'0 8px', fontSize:13, color:'#111',
                          background:'#f8fafc', cursor:'pointer', height:'100%' }}>
                        <option value="+52">🇲🇽 +52</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+54">🇦🇷 +54</option>
                        <option value="+55">🇧🇷 +55</option>
                        <option value="+56">🇨🇱 +56</option>
                        <option value="+57">🇨🇴 +57</option>
                        <option value="+506">🇨🇷 +506</option>
                        <option value="+593">🇪🇨 +593</option>
                        <option value="+503">🇸🇻 +503</option>
                        <option value="+502">🇬🇹 +502</option>
                        <option value="+504">🇭🇳 +504</option>
                        <option value="+505">🇳🇮 +505</option>
                        <option value="+507">🇵🇦 +507</option>
                        <option value="+595">🇵🇾 +595</option>
                        <option value="+51">🇵🇪 +51</option>
                        <option value="+598">🇺🇾 +598</option>
                        <option value="+58">🇻🇪 +58</option>
                        <option value="+34">🇪🇸 +34</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+49">🇩🇪 +49</option>
                      </select>
                      <input type="tel" value={waPhone}
                        onChange={e => setWaPhone(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleWARequestLink(); }}
                        placeholder="55 1234 5678"
                        autoFocus
                        style={{ flex:1, border:'1.5px solid #d1d5db', borderLeft:'none',
                          borderRadius:'0 11px 11px 0', padding:'0 14px', height:'100%',
                          fontSize:15, color:'#111', outline:'none', background:'#fff' }} />
                    </div>
                    {waPhone.replace(/\D/g,'').length > 0 && (
                      <p style={{ color:'#6b7280', fontSize:12, margin:'5px 0 0' }}>
                        Número completo: <strong style={{ color:'#111' }}>{waCountry}{waPhone.replace(/\D/g,'')}</strong>
                      </p>
                    )}
                  </div>

                  <button onClick={handleWARequestLink}
                    disabled={waSending || waPhone.replace(/\D/g,'').length < 7}
                    style={{ width:'100%', padding:'14px 0',
                      background: (waSending || waPhone.replace(/\D/g,'').length < 7)
                        ? '#d1fae5' : 'linear-gradient(135deg,#16a34a,#15803d)',
                      color: (waSending || waPhone.replace(/\D/g,'').length < 7) ? '#6b7280' : '#fff',
                      border:'none', borderRadius:13, fontSize:15, fontWeight:800,
                      cursor: (waSending || waPhone.replace(/\D/g,'').length < 7) ? 'not-allowed' : 'pointer',
                      letterSpacing:'.2px' }}>
                    {waSending ? '⏳ Generando código...' : '🔑 Obtener código de verificación →'}
                  </button>
                </div>
              )}

              {/* ════ PASO 2: Código visible → entrada DIRECTA (camino principal) ════ */}
              {waStep === 2 && (
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

                  {/* ── Banner post-WA: aparece SOLO después de tocar el botón WA ── */}
                  {waWaSent && (
                    <div style={{ background:'#dcfce7', border:'2px solid #16a34a',
                      borderRadius:12, padding:'11px 14px', textAlign:'center',
                      animation:'waSlideIn .3s ease' }}>
                      <p style={{ color:'#15803d', fontWeight:800, fontSize:13.5, margin:'0 0 3px' }}>
                        ✅ ¡Mensaje enviado por WhatsApp!
                      </p>
                      <p style={{ color:'#166534', fontSize:12, margin:0 }}>
                        Ahora escribe el código de arriba en el campo de abajo 👇
                      </p>
                    </div>
                  )}

                  {/* ── Código en pantalla — SIEMPRE visible ── */}
                  <div style={{ background:'#0f172a', borderRadius:16, padding:'16px 12px',
                    textAlign:'center' }}>
                    <p style={{ color:'#64748b', fontSize:10.5, fontWeight:700,
                      letterSpacing:1.5, textTransform:'uppercase', margin:'0 0 6px' }}>
                      Tu código OkBot
                    </p>
                    <div style={{ color:'#fff', fontSize:46, fontWeight:900,
                      fontFamily:'"Courier New",monospace', letterSpacing:16, lineHeight:1 }}>
                      {waCode6}
                    </div>
                    <p style={{ color:'#475569', fontSize:11, margin:'8px 0 0' }}>
                      Número: {waCountry}{waPhone.replace(/\D/g,'')} · válido 15 min
                    </p>
                  </div>

                  {/* ── FLECHA guía ── */}
                  <div style={{ textAlign:'center', fontSize:22, color:'#16a34a',
                    animation:'waArrowBounce 1s ease-in-out infinite', margin:'-4px 0' }}>
                    ↓
                  </div>

                  {/* ── INPUT PRINCIPAL — auto-enfocado ── */}
                  <div>
                    <p style={{ color:'#374151', fontWeight:800, fontSize:13.5,
                      margin:'0 0 8px', textAlign:'center' }}>
                      Ingresa el código de arriba aquí:
                    </p>
                    <input
                      ref={waCodeInputRef}
                      type="text"
                      inputMode="numeric"
                      value={waCode}
                      onChange={e => setWaCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                      onKeyDown={e => { if (e.key === 'Enter' && waCode.length === 6) handleWAVerify(); }}
                      placeholder="——  ——  ——  ——  ——  ——"
                      maxLength={6}
                      style={{
                        width:'100%', boxSizing:'border-box',
                        border: waCode.length === 6 ? '3px solid #16a34a' : '2.5px solid #d1d5db',
                        borderRadius:14, padding:'14px 10px',
                        fontSize:32, textAlign:'center', letterSpacing:14,
                        color: waCode.length === 6 ? '#15803d' : '#111',
                        fontFamily:'"Courier New",monospace', fontWeight:900,
                        outline:'none',
                        background: waCode.length === 6 ? '#f0fdf4' : '#fff',
                        transition:'border-color .2s, background .2s',
                        boxShadow: waCode.length === 6
                          ? '0 0 0 3px rgba(22,163,74,.15)' : 'none',
                      }}
                    />
                  </div>

                  {/* ── BOTÓN VERIFICAR — principal ── */}
                  <button onClick={handleWAVerify}
                    disabled={waVerifying || waCode.length !== 6}
                    style={{
                      width:'100%', padding:'15px 0',
                      background: waCode.length === 6
                        ? 'linear-gradient(135deg,#16a34a,#15803d)' : '#e5e7eb',
                      color: waCode.length === 6 ? '#fff' : '#9ca3af',
                      border:'none', borderRadius:13, fontSize:16, fontWeight:900,
                      cursor: waCode.length === 6 && !waVerifying ? 'pointer' : 'not-allowed',
                      transition:'all .2s',
                      boxShadow: waCode.length === 6
                        ? '0 6px 20px rgba(22,163,74,.35)' : 'none',
                    }}>
                    {waVerifying ? '⏳ Verificando...' : waCode.length === 6
                      ? '✓ Vincular WhatsApp' : `Escribe ${6 - waCode.length} dígito${6 - waCode.length !== 1 ? 's' : ''} más`}
                  </button>

                  {/* ── Sección WA opcional ── */}
                  <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px',
                    border:'1px solid #e2e8f0' }}>
                    <p style={{ color:'#94a3b8', fontSize:11.5, fontWeight:700,
                      textAlign:'center', margin:'0 0 9px',
                      letterSpacing:.5 }}>
                      ¿Prefieres verificar desde WhatsApp? (opcional)
                    </p>
                    {waWaLink ? (
                      <a href={waWaLink} target="_blank" rel="noopener noreferrer"
                        onClick={() => { setWaWaSent(true); setTimeout(() => waCodeInputRef.current?.focus(), 800); }}
                        style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                          padding:'10px 16px', background:'#25d366', borderRadius:11,
                          color:'#fff', fontSize:13.5, fontWeight:800, textDecoration:'none' }}>
                        <span>📲</span>
                        <span>Abrir WhatsApp con el código</span>
                        <span style={{ fontSize:11 }}>↗</span>
                      </a>
                    ) : (
                      <p style={{ color:'#cbd5e1', fontSize:12, textAlign:'center', margin:0 }}>
                        (número OkBot no configurado — usa entrada manual arriba)
                      </p>
                    )}
                    {waWaLink && (
                      <p style={{ color:'#94a3b8', fontSize:11, textAlign:'center',
                        margin:'7px 0 0', lineHeight:1.5 }}>
                        Envía el mensaje a OkBot, luego vuelve aquí y<br/>
                        escribe el mismo código en el campo de arriba.
                      </p>
                    )}
                  </div>

                  {/* ── Footer: cambiar / nuevo código + polling silencioso ── */}
                  <div style={{ display:'flex', justifyContent:'space-between',
                    alignItems:'center' }}>
                    <button onClick={() => { setWaStep(1); setWaCode(''); setWaCode6(''); setWaWaSent(false); }}
                      style={{ color:'#6b7280', background:'none', border:'none',
                        fontSize:12.5, cursor:'pointer', textDecoration:'underline' }}>
                      ← Cambiar número
                    </button>
                    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <div style={{ width:6, height:6, borderRadius:'50%', background:'#16a34a',
                        animation:'waPulse 2s ease-in-out infinite' }}/>
                      <span style={{ color:'#94a3b8', fontSize:11 }}>
                        verificación automática activa{waDots}
                      </span>
                    </div>
                    <button onClick={handleWARequestLink} disabled={waSending}
                      style={{ color:'#16a34a', background:'none', border:'none',
                        fontSize:12.5, cursor:'pointer', textDecoration:'underline',
                        opacity: waSending ? 0.5 : 1 }}>
                      🔄 Nuevo código
                    </button>
                  </div>
                </div>
              )}

              {/* ════ VINCULADO: éxito ════ */}
              {waStep === 'linked' && (
                <div style={{ textAlign:'center', display:'flex', flexDirection:'column',
                  alignItems:'center', gap:16 }}>
                  <div style={{ width:80, height:80, borderRadius:'50%',
                    background:'linear-gradient(135deg,#16a34a,#059669)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:'0 10px 30px rgba(22,163,74,.35)', fontSize:38 }}>
                    ✓
                  </div>
                  <div>
                    <div style={{ color:'#111', fontSize:21, fontWeight:900, marginBottom:4 }}>
                      ¡WhatsApp Vinculado! 🎉
                    </div>
                    {waLinkedPhone && (
                      <div style={{ color:'#16a34a', fontWeight:700, fontSize:14 }}>
                        {waLinkedPhone}
                      </div>
                    )}
                  </div>
                  {waOkbotPhone && (
                    <div style={{ width:'100%', background:'#eff6ff', border:'1.5px solid #bfdbfe',
                      borderRadius:12, padding:'12px 14px', textAlign:'left' }}>
                      <p style={{ color:'#1e40af', fontWeight:700, fontSize:13, margin:'0 0 6px' }}>
                        📱 Guarda <strong>OkBot</strong> en tus contactos:
                      </p>
                      <div style={{ fontFamily:'monospace', fontWeight:900, fontSize:19,
                        color:'#1e3a8a', background:'#fff', padding:'10px 14px',
                        borderRadius:9, border:'1.5px solid #bfdbfe' }}>
                        {waOkbotPhone}
                      </div>
                    </div>
                  )}
                  <div style={{ width:'100%', background:'#f0fdf4', border:'1px solid #bbf7d0',
                    borderRadius:12, padding:'12px 14px', textAlign:'left' }}>
                    <div style={{ color:'#15803d', fontWeight:700, fontSize:13, marginBottom:6 }}>
                      Escríbeme desde WhatsApp para:
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>
                      {['📝 Notas','📅 Agenda','💰 Finanzas','✅ Tareas'].map(f => (
                        <div key={f} style={{ padding:'6px 10px', background:'#fff',
                          border:'1px solid #d1fae5', borderRadius:8,
                          color:'#374151', fontSize:12.5 }}>{f}</div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:8, width:'100%' }}>
                    <button onClick={handleWAUnlink}
                      style={{ flex:1, padding:'11px 0', background:'#fff1f2',
                        color:'#dc2626', border:'1.5px solid #fecaca', borderRadius:11,
                        fontSize:13, fontWeight:700, cursor:'pointer' }}>
                      Desvincular
                    </button>
                    <button onClick={closeWAModal}
                      style={{ flex:2, padding:'11px 0',
                        background:'linear-gradient(135deg,#16a34a,#15803d)',
                        color:'#fff', border:'none', borderRadius:11,
                        fontSize:15, fontWeight:800, cursor:'pointer' }}>
                      ✓ Listo
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
          <style>{`
            @keyframes waPulse {
              0%,100%{opacity:1;transform:scale(1)}
              50%{opacity:.4;transform:scale(.65)}
            }
            @keyframes waArrowBounce {
              0%,100%{transform:translateY(0)}
              50%{transform:translateY(5px)}
            }
            @keyframes waSlideIn {
              from{opacity:0;transform:translateY(-8px)}
              to{opacity:1;transform:translateY(0)}
            }
          `}</style>
      </dialog>

      {/* ══ Google Custom Search Dialog — pantalla completa ══ */}
      <dialog
        ref={gcseDialogRef}
        onClick={(e) => { if (e.target === gcseDialogRef.current) closeGCSEModal(); }}
        style={{
          border: 'none', borderRadius: 16, padding: 0,
          width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh',
          overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
          position: 'fixed', top: 0, left: 0, margin: 0,
          background: 'transparent',
        }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg,#dc2626,#b91c1c)',
            padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)',
                display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Search size={18} color="#fff" />
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Busca en Google</div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>Buscador interno OkBot</div>
              </div>
            </div>
            <button onClick={closeGCSEModal}
              style={{ background: 'rgba(255,255,255,0.2)', border:'none', borderRadius:8,
                width:34, height:34, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <X size={18} color="#fff" />
            </button>
          </div>
          {/* Google CSE Content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px', background: '#fff' }}>
            <div className="gcse-search" style={{ minHeight: '60vh' }} />
          </div>
        </div>
      </dialog>

      {/* Messages Area - Content centered when empty, aligned to bottom when has messages */}
      <div className="chat-messages-area flex-1 min-h-0 overflow-y-auto p-4 custom-scrollbar flex flex-col">
        {messages.length === 0 ? (
          /* Empty state - centered vertically */
          <div className="chat-empty-state flex-1 flex flex-col items-center justify-center text-center px-4">
            {/* OkBot Logo - Theme aware */}
            <img 
              src={theme === 'light' ? "/okbot_light_theme.png" : "/okbot_dark_theme.png"}
              alt="OkBot" 
              className="w-24 h-24 md:w-32 md:h-32 object-contain mb-4"
            />
            <h3 className="text-xl font-semibold text-white mb-2">Hola, {user?.name}</h3>
            <p className="text-gray-500 text-sm mb-6">¿En qué puedo ayudarte hoy?</p>
            
            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
              {quickActions.map((action) => {
                const Icon = action.icon;
                const colorMap = {
                  green:  { bg:'#16a34a', hover:'#15803d', border:'rgba(22,163,74,0.5)' },
                  red:    { bg:'#dc2626', hover:'#b91c1c', border:'rgba(220,38,38,0.5)' },
                  orange: { bg:'#ea580c', hover:'#c2410c', border:'rgba(234,88,12,0.5)' },
                  blue:   { bg:'#2563eb', hover:'#1d4ed8', border:'rgba(37,99,235,0.5)' },
                };
                const c = colorMap[action.color] || {};
                const isColored = !!action.color;
                return (
                  <button key={action.id} onClick={() => handleQuickAction(action)}
                    className="p-3 flex items-center gap-2 transition-colors text-left rounded-xl border"
                    style={isColored ? {
                      backgroundColor: c.bg,
                      borderColor: c.border,
                      color: '#ffffff',
                    } : { borderColor: 'transparent' }}
                    onMouseEnter={e => { if (isColored) e.currentTarget.style.backgroundColor = c.hover; }}
                    onMouseLeave={e => { if (isColored) e.currentTarget.style.backgroundColor = c.bg; }}>
                    <Icon className="w-4 h-4 flex-shrink-0" style={{ color:'#ffffff', stroke:'#ffffff' }} />
                    <span className="text-xs font-medium" style={{ color: '#ffffff' }}>{action.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Messages - grow from bottom */
          <div className="flex-1 flex flex-col justify-end">
            {messages.map((msg, idx) => (
          <div key={idx} className={`mb-3 flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className="max-w-[85%]">
              {msg.role === "assistant" ? (
                <div className="flex items-start gap-2">
                  {/* OkBot avatar */}
                  <img
                    src="/okbot_logo_2026.png"
                    alt="OkBot"
                    className="w-8 h-8 rounded-full flex-shrink-0 mt-0.5 shadow-md object-cover"
                    onError={(e) => { e.target.style.display='none'; }}
                  />
                  <div className="chat-bubble-bot flex-1">
                  <RichMessage content={msg.content} onMediaClick={(src, type, items) => setLightboxMedia({ content: src, type, items: items || null, idx: items ? Math.max(0, items.indexOf(src)) : 0 })} />
                  
                  {/* Action bar: Listen + Share */}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <button 
                      onClick={() => isSpeaking ? stopSpeaking() : speakResponse(msg.content)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 transition-colors"
                      data-testid="listen-response-btn"
                    >
                      {isSpeaking ? <VolumeX size={14} /> : <Volume2 size={14} />}
                      {isSpeaking ? t('stop') : t('listen')}
                    </button>
                    {/* Share panel */}
                    <SharePanel content={msg.content} waPhone={waLinkedPhone} />
                  </div>
                  
                  {/* Executed Actions confirmation */}
                  {msg.executedActions && msg.executedActions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {msg.executedActions.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-500/15 text-green-400 border border-green-500/25">
                          <span>✓</span> {a.message || `Acción ejecutada`}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Suggested Actions */}
                  {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.suggestedActions.map((action, i) => (
                        <button key={i} onClick={() => setInput(action)}
                          className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                          {action}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* ⚡ Super Fast — "Dame más detalles" button */}
                  {msg.superFast && idx === messages.length - 1 && !loading && (
                    <button
                      onClick={() => sendMessage(msg.originalQuery, true)}
                      className="mt-3 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                    >
                      <span>⚡</span> {t('moreDetail')}
                    </button>
                  )}
                  </div>
                </div>
              ) : (
                <div className="flex items-end gap-2.5 justify-end">
                  {/* User Bubble */}
                  <div className="user-bubble-modern">
                    <p className="leading-relaxed text-sm">{msg.content}</p>
                  </div>
                  {/* User Avatar — large, right side */}
                  {userPhoto ? (
                    <img src={userPhoto} alt="Tu foto"
                      className="w-10 h-10 rounded-2xl object-cover flex-shrink-0 shadow-lg ring-2 ring-white/20" />
                  ) : (
                    <div className="w-10 h-10 rounded-2xl flex-shrink-0 flex items-center justify-center text-white font-bold text-base shadow-lg ring-2 ring-white/20"
                         style={{ background: 'linear-gradient(135deg, #dc2626 0%, #7c3aed 100%)' }}>
                      {user?.okuser?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start mb-3">
            <div className="chat-bubble-bot">
              <div className="typing-indicator">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area - Simplified */}
      <div className="border-t border-white/5 p-3 sticky bottom-0" style={{backdropFilter:'blur(12px)'}}>
        {/* Mic bubble — floating above-right of the input, outside the glass panel */}
        <div className="flex justify-end pr-1 mb-2">
          <button
            onClick={handleVoice}
            title={listening ? 'Detener grabación' : 'Grabar voz'}
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: listening ? '#ef4444' : '#111111',
              boxShadow: listening
                ? '0 0 0 0 rgba(239,68,68,0.6)'
                : '0 2px 12px rgba(0,0,0,0.5)',
              animation: listening ? 'micPulse 1.2s ease-out infinite' : 'none',
              transition: 'background 0.25s ease, box-shadow 0.25s ease',
              position: 'relative',
            }}
          >
            {listening
              ? <MicOff size={22} color="white" />
              : <Mic size={22} color="white" />
            }
            {listening && (
              <span style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'rgba(239,68,68,0.35)',
                animation: 'micRipple 1.2s ease-out infinite',
              }} />
            )}
          </button>
        </div>

        <div className="glass-panel rounded-xl p-2">
          <textarea 
            ref={textareaRef}
            value={input} 
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={t('chatPlaceholder')}
            className="w-full bg-transparent text-white placeholder-gray-500 resize-none outline-none text-sm min-h-[72px] max-h-[200px]"
            rows={3}
          />
          
          {/* Bottom controls — responsive two-row layout */}
          <div className="mt-2 pt-2 border-t border-white/5 flex flex-col gap-1.5">
            {/* Row 1: Model selector + action buttons */}
            <div className="flex items-center gap-1 min-w-0">
              {/* Icon buttons — shrink-0 so they never collapse */}
              <button onClick={() => setShowHistory(true)} 
                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors" title="Historial">
                <History size={15} />
              </button>
              
              {conversationId && (
                <button onClick={() => setShowShareModal(true)} 
                  className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-blue-400 transition-colors" title="Compartir">
                  <Share2 size={15} />
                </button>
              )}
              
              <button onClick={() => { setMessages([]); setConversationId(null); try { localStorage.removeItem('okbot_last_conv_id'); localStorage.removeItem('okbot_last_conv_msgs'); } catch {} }} 
                className="flex-shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors" title="Nueva conversación">
                <RefreshCw size={15} />
              </button>

              {/* Fish Audio auto-speak toggle */}
              <button
                onClick={() => {
                  const next = !autoSpeak;
                  setAutoSpeak(next);
                  if (next && fishAudioReady === false) {
                    toast.info('Fish Audio no configurado — usa el ícono 🔊 para escuchar manualmente', { duration: 3500 });
                  } else if (next) {
                    toast.success('Auto-voz activada 🎙️ — OkBot hablará sus respuestas', { duration: 2000 });
                  } else {
                    toast.info('Auto-voz desactivada', { duration: 1500 });
                  }
                }}
                className={`flex-shrink-0 flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                  autoSpeak
                    ? 'bg-orange-500/20 border-orange-500/40 text-orange-400 hover:bg-orange-500/30'
                    : 'bg-white/5 border-white/10 text-gray-500 hover:text-gray-300 hover:bg-white/10'
                }`}
                title={autoSpeak ? 'Auto-voz activa (Fish Audio) — clic para desactivar' : 'Activar auto-voz Fish Audio'}
              >
                {autoSpeak ? <Volume2 size={13} /> : <VolumeX size={13} />}
                <span className="hidden sm:inline">
                  {autoSpeak
                    ? (fishAudioReady ? `🐟 ${t('voice')}` : `🔊 ${t('voice')}`)
                    : t('voice')}
                </span>
              </button>

              {/* Language picker for Fish Audio TTS */}
              {(() => {
                const LANGS = [
                  { code: 'auto', flag: '🌐', label: 'Auto' },
                  { code: 'es',   flag: '🇪🇸', label: 'Español' },
                  { code: 'en',   flag: '🇺🇸', label: 'English' },
                  { code: 'pt',   flag: '🇧🇷', label: 'Português' },
                  { code: 'fr',   flag: '🇫🇷', label: 'Français' },
                  { code: 'de',   flag: '🇩🇪', label: 'Deutsch' },
                  { code: 'it',   flag: '🇮🇹', label: 'Italiano' },
                  { code: 'ja',   flag: '🇯🇵', label: '日本語' },
                  { code: 'zh',   flag: '🇨🇳', label: '中文' },
                  { code: 'ko',   flag: '🇰🇷', label: '한국어' },
                  { code: 'ar',   flag: '🇸🇦', label: 'العربية' },
                  { code: 'ru',   flag: '🇷🇺', label: 'Русский' },
                  { code: 'nl',   flag: '🇳🇱', label: 'Nederlands' },
                  { code: 'tr',   flag: '🇹🇷', label: 'Türkçe' },
                  { code: 'pl',   flag: '🇵🇱', label: 'Polski' },
                  { code: 'id',   flag: '🇮🇩', label: 'Indonesia' },
                ];
                const current = LANGS.find(l => l.code === ttsLanguage) || LANGS[0];
                return (
                  <div ref={langPickerRef} className="relative flex-shrink-0">
                    <button
                      onClick={() => setShowLangPicker(p => !p)}
                      className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                      title={`Idioma de voz: ${current.label}`}
                    >
                      <span>{current.flag}</span>
                      <span className="hidden sm:inline">{current.code === 'auto' ? 'Auto' : current.code.toUpperCase()}</span>
                    </button>
                    {showLangPicker && (
                      <div className="absolute bottom-full left-0 mb-2 z-50 bg-[#1a1f2e] border border-white/15 rounded-xl shadow-2xl py-1.5 min-w-[150px] max-h-72 overflow-y-auto">
                        <p className="px-3 py-1 text-[10px] text-gray-500 uppercase tracking-wider font-medium">Idioma de voz</p>
                        {LANGS.map(lang => (
                          <button
                            key={lang.code}
                            onClick={() => {
                              setTtsLanguage(lang.code);
                              setShowLangPicker(false);
                              toast.success(`Voz en ${lang.label} ${lang.flag}`, { duration: 1500 });
                            }}
                            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-left transition-colors hover:bg-white/10 ${
                              ttsLanguage === lang.code ? 'text-orange-400 bg-orange-500/10' : 'text-gray-300'
                            }`}
                          >
                            <span className="text-base">{lang.flag}</span>
                            <span>{lang.label}</span>
                            {ttsLanguage === lang.code && <span className="ml-auto text-orange-400">✓</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Model select — flex-1 so it takes remaining space, min-w-0 prevents overflow */}
              <select value={modelType} onChange={(e) => setModelType(e.target.value)}
                className="flex-1 min-w-0 bg-white/5 text-gray-400 text-xs rounded-lg px-1.5 py-1.5 outline-none cursor-pointer border border-white/10 hover:border-white/20 transition-colors"
                title="Seleccionar modelo de IA">
                <optgroup label="── Combos (NIM Powered) ──">
                  <option value="combo-free">⚡ Esencial — NIM Flash + Free</option>
                  <option value="super-combo">🚀 Super Combo — NIM 70B+</option>
                  <option value="combo-paid">💎 Premium — NIM 120B Ultra</option>
                </optgroup>
                <optgroup label="🟢 NVIDIA NIM — Rápidos (sin espera)">
                  <option value="nim:deepseek-v4-flash">🟢 DeepSeek V4 Flash ⚡ (más rápido)</option>
                  <option value="nim:gpt-oss-20b">🟢 OpenAI GPT-OSS 20B ⚡</option>
                  <option value="nim:llama-3.2-3b">🟢 Llama 3.2 3B ⚡ (ultra-ligero)</option>
                </optgroup>
                <optgroup label="🟢 NVIDIA NIM — Potentes">
                  <option value="nim:deepseek-v4-pro">🟢 DeepSeek V4 Pro</option>
                  <option value="nim:llama-70b">🟢 Meta Llama 3.3 70B</option>
                  <option value="nim:nemotron-70b">🟢 NVIDIA Nemotron 70B</option>
                  <option value="nim:nemotron-super-49b">🟢 NVIDIA Nemotron Super 49B</option>
                  <option value="nim:mistral-large">🟢 Mistral Large</option>
                  <option value="nim:kimi-k2">🟢 Kimi K2</option>
                </optgroup>
                <optgroup label="🟢 NVIDIA NIM — Ultra (250B+)">
                  <option value="nim:gpt-oss-120b">🟢 OpenAI GPT-OSS 120B</option>
                  <option value="nim:nemotron-ultra-253b">🟢 NVIDIA Nemotron Ultra 253B</option>
                  <option value="nim:qwen-397b">🟢 Qwen 3.5 397B</option>
                  <option value="nim:nemotron-550b">🟢 NVIDIA Nemotron 550B</option>
                  <option value="nim:mistral-large-675b">🟢 Mistral Large 3 675B</option>
                </optgroup>
                {dynamicModels.filter(m => m.id.includes(':free')).length > 0 ? (
                  <optgroup label="Nivel Esencial">
                    {dynamicModels.filter(m => m.id.includes(':free')).map(m => (
                      <option key={m.id} value={m.id}>{m.name} - {m.provider}</option>
                    ))}
                  </optgroup>
                ) : null}
                {dynamicModels.filter(m => !m.id.includes(':free')).length > 0 ? (
                  <optgroup label="🔒 Premium — Requiere plan">
                    {dynamicModels.filter(m => !m.id.includes(':free')).map(m => (
                      <option key={m.id} value={`unlock:${m.id}`}>🔒 {m.name}</option>
                    ))}
                  </optgroup>
                ) : null}
                <optgroup label="🔒 Próximamente">
                  <option value="unlock:gpt5" disabled>🔒 GPT-5.2</option>
                  <option value="unlock:gemini3" disabled>🔒 Gemini 3</option>
                </optgroup>
              </select>

              {/* Plan upgrade button — only shows when locked model selected */}
              {(modelType?.startsWith('unlock:')) && (
                <button
                  onClick={() => { if (window.goToSettings) window.goToSettings('plans'); else toast.info('Ve a Configuración → Planes OkBot para desbloquear'); }}
                  className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 rounded-lg text-xs font-medium transition-colors border border-purple-500/30"
                  title="Desbloquear modelos premium">
                  🔓
                </button>
              )}
            </div>

            {/* Row 2: OkBot Quick Menu + Support + send */}
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1.5">
                {/* OkBot Quick Menu logo — bulletproof open via pointerDown */}
                <button
                  type="button"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowQuickMenu(true);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowQuickMenu(true);
                  }}
                  className="flex-shrink-0 p-1 rounded-lg hover:bg-indigo-50 transition-all relative cursor-pointer ring-1 ring-transparent hover:ring-indigo-200"
                  style={{ zIndex: 10, pointerEvents: 'auto', touchAction: 'manipulation' }}
                  title="Menú rápido OkBot"
                  data-testid="okbot-quick-menu-btn"
                  aria-label="Abrir menú rápido OkBot"
                >
                  <img
                    src={theme === 'light' ? "/okbot_light_theme.png" : "/okbot_dark_theme.png"}
                    alt="OkBot"
                    className="w-7 h-7 object-contain"
                    style={{ pointerEvents: 'none' }}
                    draggable={false}
                  />
                </button>
                {/* Support button */}
                <button 
                  onClick={() => setShowSupportChat(true)}
                  className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 bg-red-500 hover:bg-red-600 rounded-lg transition-all text-xs font-medium text-white"
                  data-testid="support-okgo-btn"
                >
                  <HelpCircle size={13} style={{ color: 'white' }} />
                  <span className="hidden xs:inline" style={{ color: 'white' }}>Soporte</span>
                </button>
              </div>

              {/* Send button */}
              <button onClick={() => sendMessage()} disabled={loading || !input.trim()} 
                className="flex-shrink-0 p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white disabled:opacity-50 transition-colors"
                title="Enviar mensaje">
                <ArrowUp size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Support Chat Modal */}
      <UserSupportChat 
        user={user} 
        isOpen={showSupportChat} 
        onClose={() => setShowSupportChat(false)} 
      />

      {/* OkBot Quick Menu Modal — Portal a document.body, theme-aware, robusto */}
      {showQuickMenu && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{
            zIndex: 2147483647,
            backgroundColor: theme === 'light' ? 'rgba(15,23,42,0.55)' : 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            pointerEvents: 'auto',
          }}
          onPointerDown={(e) => { if (e.target === e.currentTarget) setShowQuickMenu(false); }}
          data-testid="okbot-quick-menu-modal"
        >
          <div
            className={`rounded-2xl max-w-sm w-full p-6 shadow-2xl relative border ${
              theme === 'light'
                ? 'bg-white border-slate-200 text-slate-900'
                : 'bg-[#0d1117] border-white/10 text-white'
            }`}
            onPointerDown={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowQuickMenu(false)}
              className={`absolute top-3 right-3 p-1.5 rounded-lg ${
                theme === 'light'
                  ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              aria-label="Cerrar"
              data-testid="okbot-quick-menu-close"
            >
              <X size={18} />
            </button>
            <div className="flex flex-col items-center text-center">
              <img
                src={theme === 'light' ? "/okbot_light_theme.png" : "/okbot_dark_theme.png"}
                alt="OkBot"
                className="w-20 h-20 md:w-24 md:h-24 object-contain mb-3"
                draggable={false}
              />
              <h3 className={`text-lg font-semibold mb-1 ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>
                Hola, {user?.name}
              </h3>
              <p className={`text-sm mb-5 ${theme === 'light' ? 'text-slate-500' : 'text-gray-500'}`}>
                ¿En qué puedo ayudarte hoy?
              </p>

              <div className="grid grid-cols-2 gap-2 w-full">
                {(quickActions || []).map((action) => {
                  const Icon = action.icon;
                  const colorMap = {
                    green:  { bg:'#16a34a', hover:'#15803d', border:'rgba(22,163,74,0.5)' },
                    red:    { bg:'#dc2626', hover:'#b91c1c', border:'rgba(220,38,38,0.5)' },
                    orange: { bg:'#ea580c', hover:'#c2410c', border:'rgba(234,88,12,0.5)' },
                    blue:   { bg:'#2563eb', hover:'#1d4ed8', border:'rgba(37,99,235,0.5)' },
                  };
                  const c = colorMap[action.color] || { bg:'#4f46e5', hover:'#4338ca', border:'rgba(79,70,229,0.5)' };
                  return (
                    <button
                      key={action.id}
                      type="button"
                      onClick={() => { setShowQuickMenu(false); handleQuickAction(action); }}
                      className="p-3 flex items-center gap-2 transition-colors text-left rounded-xl border"
                      style={{ backgroundColor: c.bg, borderColor: c.border, color: '#fff' }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = c.hover; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = c.bg; }}
                    >
                      {Icon ? <Icon className="w-4 h-4 flex-shrink-0" style={{ color:'#fff', stroke:'#fff' }} /> : null}
                      <span className="text-xs font-medium" style={{ color:'#fff' }}>{action.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ChatModule;
